import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { MedicalDisclaimer } from '@/components/MedicalDisclaimer';
import { AlertTriangle, ShieldAlert, Loader2, X } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const SEVERITY_STYLES = {
  danger: { bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-200/60 dark:border-red-700/30', icon: 'text-red-600 dark:text-red-400', text: 'text-red-800 dark:text-red-300' },
  warning: { bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-200/60 dark:border-amber-700/30', icon: 'text-amber-600 dark:text-amber-400', text: 'text-amber-800 dark:text-amber-300' },
  caution: { bg: 'bg-blue-50 dark:bg-blue-900/10', border: 'border-blue-200/60 dark:border-blue-700/30', icon: 'text-blue-600 dark:text-blue-400', text: 'text-blue-800 dark:text-blue-300' },
};

export function FoodSafetyAlert({ items = [], onDismiss }) {
  const { authHeaders, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());

  const mode = user?.health_mode || 'general';

  useEffect(() => {
    if (mode === 'general' || !items.length) {
      setAlerts([]);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/health-modes/check-food-safety`, { items }, authHeaders());
        setAlerts(res.data.alerts || []);
      } catch { }
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, mode]);

  if (mode === 'general' || !alerts.length || loading) return null;

  const visible = alerts.filter(a => !dismissed.has(a.item_name + a.flag_name));
  if (!visible.length) return null;

  return (
    <div className="space-y-2" data-testid="food-safety-alerts">
      {visible.map((alert, i) => {
        const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.caution;
        return (
          <div key={i} className={`flex items-start gap-2.5 p-3 rounded-xl border ${style.bg} ${style.border}`}>
            {alert.severity === 'danger' ? (
              <ShieldAlert className={`w-4 h-4 flex-shrink-0 mt-0.5 ${style.icon}`} />
            ) : (
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${style.icon}`} />
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold ${style.text}`}>{alert.flag_name}</p>
              <p className={`text-[10px] mt-0.5 ${style.text} opacity-80`}>
                <span className="font-medium">{alert.item_name}</span> — {alert.message}
              </p>
            </div>
            <button
              onClick={() => setDismissed(prev => new Set([...prev, alert.item_name + alert.flag_name]))}
              className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
            >
              <X className="w-3 h-3 text-[#7A7A72]" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
